import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  CardActions,
  Divider,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  FormGroup,
  FormControlLabel,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import { Delete as DeleteIcon, Assignment as AssignmentIcon } from '@mui/icons-material';
import { examAPI, userAPI } from '../services/api';
import {
  fetchExamsStart,
  fetchExamsSuccess,
  fetchExamsFailure,
} from '../store/slices/examSlice';

const AdminDashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const { exams, loading, error } = useSelector((state) => state.exam);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [examToDelete, setExamToDelete] = useState(null);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [examToAssign, setExamToAssign] = useState(null);
  const [students, setStudents] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [assignError, setAssignError] = useState(null);

  useEffect(() => {
    fetchExams();
  }, [dispatch]);

  const fetchExams = async () => {
    dispatch(fetchExamsStart());
    try {
      const response = await examAPI.getExams();
      if (response.data.success) {
        dispatch(fetchExamsSuccess(response.data));
      } else {
        dispatch(fetchExamsFailure(response.data.message || 'Failed to fetch exams'));
      }
    } catch (error) {
      console.error('Error fetching exams:', error);
      dispatch(fetchExamsFailure(error.response?.data?.message || 'Failed to fetch exams'));
    }
  };

  const fetchStudents = async () => {
    try {
      const response = await userAPI.getStudents();
      if (response.data.success) {
        setStudents(response.data.students || []);
      }
    } catch (error) {
      console.error('Error fetching students:', error);
      setAssignError('Failed to fetch students');
    }
  };

  const handleCreateExam = () => {
    console.log('Create Exam clicked, user role:', user?.role);
    navigate('/create-exam');
  };

  const handleManageStudents = () => {
    navigate('/students');
  };

  const handleViewResults = (examId) => {
    navigate(`/results/${examId}`);
  };

  const handleDeleteClick = (exam) => {
    setExamToDelete(exam);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      const response = await examAPI.deleteExam(examToDelete.id);
      if (response.data.success) {
        fetchExams();
        setDeleteDialogOpen(false);
        setExamToDelete(null);
      } else {
        dispatch(fetchExamsFailure(response.data.message || 'Failed to delete exam'));
      }
    } catch (error) {
      console.error('Error deleting exam:', error);
      dispatch(fetchExamsFailure(error.response?.data?.message || 'Failed to delete exam'));
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setExamToDelete(null);
  };

  const handleAssignClick = async (exam) => {
    setExamToAssign(exam);
    setSelectedStudents([]);
    setAssignError(null);
    await fetchStudents();
    setAssignDialogOpen(true);
  };

  const handleAssignConfirm = async () => {
    try {
      const studentIds = selectedStudents.map(student => student.id);
      const response = await examAPI.assignExam(examToAssign.id, studentIds);
      if (response.data.success) {
        setAssignDialogOpen(false);
        setExamToAssign(null);
        setSelectedStudents([]);
        fetchExams(); // Refresh the exams list
      } else {
        setAssignError(response.data.message || 'Failed to assign exam');
      }
    } catch (error) {
      console.error('Error assigning exam:', error);
      setAssignError(error.response?.data?.message || 'Failed to assign exam');
    }
  };

  const handleAssignCancel = () => {
    setAssignDialogOpen(false);
    setExamToAssign(null);
    setSelectedStudents([]);
    setAssignError(null);
  };

  const handleStudentSelect = (student) => {
    setSelectedStudents(prev => {
      const isSelected = prev.some(s => s.id === student.id);
      if (isSelected) {
        return prev.filter(s => s.id !== student.id);
      } else {
        return [...prev, student];
      }
    });
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
            <Typography component="h1" variant="h4" gutterBottom>
              Welcome, {user?.fullName}
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Admin Dashboard
            </Typography>
          </Paper>
        </Grid>

        {error && (
          <Grid item xs={12}>
            <Alert severity="error">{error}</Alert>
          </Grid>
        )}

        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6">Quick Actions</Typography>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleCreateExam}
                  sx={{ height: '100%', py: 2 }}
                >
                  Create New Exam
                </Button>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleManageStudents}
                  sx={{ height: '100%', py: 2 }}
                >
                  Manage Students
                </Button>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Available Exams
            </Typography>
            <Grid container spacing={3}>
              {loading ? (
                <Grid item xs={12}>
                  <Typography>Loading exams...</Typography>
                </Grid>
              ) : !exams || exams.length === 0 ? (
                <Grid item xs={12}>
                  <Typography>No exams available.</Typography>
                </Grid>
              ) : (
                exams.map((exam) => (
                  <Grid item xs={12} sm={6} md={4} key={exam.id}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          {exam.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Duration: {exam.time_limit} minutes
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Assigned Students: {exam.assigned_students || 0}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Total Attempts: {exam.total_attempts || 0}
                        </Typography>
                      </CardContent>
                      <Divider />
                      <CardActions>
                        <Button
                          size="small"
                          color="primary"
                          onClick={() => handleViewResults(exam.id)}
                          sx={{ mr: 1 }}
                        >
                          View Results
                        </Button>
                        <Button
                          size="small"
                          color="primary"
                          startIcon={<AssignmentIcon />}
                          onClick={() => handleAssignClick(exam)}
                          sx={{ mr: 1 }}
                        >
                          Assign
                        </Button>
                        <Button
                          size="small"
                          color="error"
                          startIcon={<DeleteIcon />}
                          onClick={() => handleDeleteClick(exam)}
                        >
                          Delete
                        </Button>
                      </CardActions>
                    </Card>
                  </Grid>
                ))
              )}
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
      >
        <DialogTitle>Delete Exam</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the exam "{examToDelete?.title}"? This action cannot be undone.
            All exam attempts and student answers will be permanently deleted.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign Exam Dialog */}
      <Dialog
        open={assignDialogOpen}
        onClose={handleAssignCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Assign Exam to Students</DialogTitle>
        <DialogContent>
          {assignError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {assignError}
            </Alert>
          )}
          <DialogContentText sx={{ mb: 2 }}>
            Select students to assign the exam "{examToAssign?.title}"
          </DialogContentText>
          <List sx={{ width: '100%', maxHeight: 400, overflow: 'auto' }}>
            {students.map((student) => (
              <ListItem key={student.id} dense button onClick={() => handleStudentSelect(student)}>
                <ListItemIcon>
                  <Checkbox
                    edge="start"
                    checked={selectedStudents.some(s => s.id === student.id)}
                    tabIndex={-1}
                    disableRipple
                  />
                </ListItemIcon>
                <ListItemText
                  primary={student.fullName}
                  secondary={student.email}
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAssignCancel}>Cancel</Button>
          <Button
            onClick={handleAssignConfirm}
            color="primary"
            disabled={selectedStudents.length === 0}
          >
            Assign
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminDashboard;